/**
 * How deep a paginated list was, remembered across navigations (#back).
 *
 * THE PROBLEM. Every catalog view is disposed and re-mounted on each
 * navigation, and a fresh `VirtualList` starts at `windowSize` rows having
 * fetched exactly one page. So tapping into an album from 300 artists down
 * `/browse/artists` and pressing Back re-mounts a 40-row list — and the saved
 * scroll position cannot be honoured, because the tall document it belonged to
 * no longer exists.
 *
 * WHAT THIS STORES — and, deliberately, what it does NOT. Only two small
 * numbers per route: how many PAGES had been fetched and how far the render
 * window had been opened. The rows themselves are not duplicated here, because
 * `api-client.ts` already caches every catalog page for 24h under the same TTL
 * (`catalogKey` includes the cursor, so page 7 is cached as surely as page 1 —
 * the library is static, see the project's static-library note). Replaying N
 * page reads on return is therefore N cache hits: no network, no spinner, and
 * no second copy of the catalog competing for the storage quota.
 *
 * Backed by the same guarded {@link createTtlCache} the catalog uses, so a
 * blocked or full storage degrades to "no memory" — the list simply behaves as
 * it did before this existed — and an entry older than the catalog cache it
 * depends on expires with it rather than replaying pages that are gone.
 *
 * @module
 */

import { DEFAULT_TTL_MS, createTtlCache, type TtlCache } from './ttl-cache.js';

/** Matches the catalog page cache's lifetime — the pages this replays. */
export const LIST_DEPTH_TTL_MS = DEFAULT_TTL_MS;

/**
 * Most pages one restore will replay. A bound, not a policy: it caps the work
 * a corrupt or hand-edited entry can demand, and 50 pages is far beyond any
 * real scroll (2,000 rows at the 40-row page size, on a 1,010-artist library).
 */
export const MAX_RESTORE_PAGES = 50;

/** What one route remembers. */
export interface ListDepthRecord {
  /** Pages fetched, >= 1. */
  readonly pages: number;
  /** Rows the render window had been opened to. */
  readonly shown: number;
}

/** The per-route handle a view is given. */
export interface ListDepth {
  /** The remembered depth, or null when there is nothing (or it expired). */
  read(): ListDepthRecord | null;
  /** Record the current depth. Silent on failure. */
  remember(pages: number, shown: number): void;
  /** Forget this route (the data underneath changed). */
  clear(): void;
}

export interface ListDepthStore {
  /** A handle for one route key, e.g. `artists` or `genre:<id>`. */
  for(key: string): ListDepth;
}

function isRecord(value: unknown): value is ListDepthRecord {
  if (typeof value !== 'object' || value === null) return false;
  const { pages, shown } = value as Partial<ListDepthRecord>;
  return typeof pages === 'number' && typeof shown === 'number';
}

/**
 * @param deps `cache` is injectable so a suite can drive the memory without
 *   touching real storage; `maxPages` bounds one replay.
 */
export function createListDepthStore(
  deps: { readonly cache?: TtlCache; readonly maxPages?: number } = {},
): ListDepthStore {
  const cache =
    deps.cache ?? createTtlCache({ namespace: 'sm.depth.', ttlMs: LIST_DEPTH_TTL_MS });
  const maxPages = deps.maxPages ?? MAX_RESTORE_PAGES;

  return {
    for(key: string): ListDepth {
      return {
        read(): ListDepthRecord | null {
          const raw = cache.get<unknown>(key);
          // PERSISTED DATA IS INPUT: another tab or an older build could have
          // written anything. A record that does not validate is treated as
          // absent, never trusted into the replay loop below.
          if (!isRecord(raw)) return null;
          const pages = Math.floor(raw.pages);
          const shown = Math.floor(raw.shown);
          if (!Number.isFinite(pages) || !Number.isFinite(shown)) return null;
          if (pages < 1 || shown < 1) return null;
          return { pages: Math.min(pages, maxPages), shown };
        },

        remember(pages: number, shown: number): void {
          if (!Number.isFinite(pages) || !Number.isFinite(shown)) return;
          // Page 1 at the default window is the state a fresh mount already
          // produces — storing it would be a write per navigation for nothing.
          if (pages <= 1 && shown <= 0) return;
          cache.set(key, {
            pages: Math.max(1, Math.min(Math.floor(pages), maxPages)),
            shown: Math.max(1, Math.floor(shown)),
          });
        },

        clear(): void {
          cache.delete(key);
        },
      };
    },
  };
}
